<template>
    <div>
        <headline>
            Konferenzräume
            <template v-slot:more>
                <conference-room-booking-create ref="create" class="mr-2" @created="load"></conference-room-booking-create>
                <conference-room-create class="mr-2" @created="load"></conference-room-create>
                <conference-rooms-edit></conference-rooms-edit>
            </template>
        </headline>

        <loading v-show="loading"></loading>

        <div class="mb-8">
            <ul class="mt-3 grid grid-cols-1 gap-5 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4">
                <li>
                    <a class="col-span-1 flex shadow-sm rounded-md" href="/admin/pages/conference_rooms">
                        <div
                            class="flex-shrink-0 flex items-center justify-center w-16 bg-yellow-500 text-white text-sm font-medium rounded-l-md">
                            K
                        </div>
                        <div
                            class="flex-1 flex items-center justify-between border-t border-r border-b border-gray-200 bg-white rounded-r-md truncate">
                            <div class="flex-1 px-4 py-2 text-sm truncate">
                                <span class="text-gray-900 font-medium hover:text-gray-600">Konferenzräume</span>
                                <p class="text-gray-500"> {{ items.length }} </p>
                            </div>
                            <div class="flex-shrink-0 pr-2">
                            </div>
                        </div>
                    </a>
                </li>
            </ul>
        </div>

        <select-menu class="mb-4" v-show="floors.length > 1"  v-model="floor"
                     :values="['all',null,...floors.map(g => g.id)]"
                     :text="['Alle','Ohne Etage',...floors.map(g => g.name)]">
            Etage
        </select-menu>

        <navigation-floor v-if="floor !== 'all' && floor !== ''" :items="available" :unavailable="unavailable" @selected="bookConferenceRoom"></navigation-floor>

        <accordion>
            <accordion-item v-for="item in conferenceRooms" :key="item.id" :id="item.id" :open="roomToOpen" :ref="'room' + item.id">
                <template v-slot:header>
                    <span class="fa fa-users mr-2"></span>
                    <span>{{ item.name }}</span>
                    <i v-show="item.pending_bookings_count > 0" class="fa fa-fw fa-bell-o mr-2 text-yellow-500"></i>
                </template>
                <div class="flex justify-end">
                    <a :href="`/panel/${item.uuid}`" :item="item" class="mr-2">
                        <button-secondary>
                            <template slot="icon"><i class="fa fa-television"></i></template>
                            Buchungsmonitor anzeigen
                        </button-secondary>
                    </a>
                    <conference-room-edit :item="item" :rooms="conferenceRooms" @updated="load"></conference-room-edit>
                </div>

                <div class="border-gray-200 px-4 py-5 sm:px-6">
                    <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                        <div class="sm:col-span-1">
                            <dt class="text-sm font-medium text-gray-500">
                                Name
                            </dt>
                            <dd class="mt-1 text-sm text-gray-900">
                                {{ item.name }}
                            </dd>
                        </div>
                        <div class="sm:col-span-1">
                            <dt class="text-sm font-medium text-gray-500">
                                max. Anzahl Personen
                            </dt>
                            <dd class="mt-1 text-sm text-gray-900">
                                {{ item.persons }}
                            </dd>
                        </div>
                        <div class="sm:col-span-1">
                            <dt class="text-sm font-medium text-gray-500">
                                Feste Ausstattung
                            </dt>
                            <dd class="mt-1 text-sm text-gray-900">
                                <span v-show="item.equipment.length === 0">-</span>
                                <span v-for="equipment in item.equipment"
                                      class="mr-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800">
                                    {{ equipment.name }}
                                </span>
                            </dd>
                        </div>
                        <div class="sm:col-span-1">
                            <dt class="text-sm font-medium text-gray-500">
                                Zubuchbare Ausstattung
                            </dt>
                            <dd class="mt-1 text-sm text-gray-900">
                                <span v-show="item.postable_equipment.length === 0">-</span>
                                <span v-for="equipment in item.postable_equipment"
                                      class="mr-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800">
                                    {{ equipment.name }}
                                </span>
                            </dd>
                        </div>
                        <div class="sm:col-span-1" v-show="item.room_layouts.length > 0">
                            <dt class="text-sm font-medium text-gray-500">
                                Raumlayouts
                            </dt>
                            <dd class="mt-1 text-sm text-gray-900">
                                <span v-for="layout in item.room_layouts"
                                      class="mr-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800">
                                    {{ layout.name }}
                                </span>
                            </dd>
                        </div>
                        <div class="sm:col-span-1" v-show="item.caterings.length > 0">
                            <dt class="text-sm font-medium text-gray-500">
                                Catering
                            </dt>
                            <dd class="mt-1 text-sm text-gray-900">
                                <span v-for="catering in item.caterings"
                                      class="mr-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800">
                                    {{ catering.name }}
                                </span>
                            </dd>
                        </div>
                        <div class="sm:col-span-1">
                            <dt class="text-sm font-medium text-gray-500">
                                Bestätigung beim Buchen erforderlich
                            </dt>
                            <dd class="mt-1 text-sm text-gray-900">
                                {{ item.requires_confirmation ? 'Ja' : 'Nein' }}
                            </dd>
                        </div>
                    </dl>
                </div>

                <p v-show="item.bookings.length > 0" class="ml-4 mb-4 font-semibold">
                    Buchungen für heute
                </p>
                <p v-show="item.bookings.length === 0" class="ml-4 mb-4 font-semibold">
                    Keine Buchungen für heute gefunden.
                </p>

                <tailwind-table class="mb-4" :header="['Datum', 'Bucher', 'Anmerkung', 'Serienbuchung','']"
                                v-show="item.bookings.length > 0">
                    <tr v-for="(booking, i) in sortBookings(item.bookings)" :class="{'bg-gray-50': i % 2}">
                        <td class="px-6 py-3">{{ booking.start | dayOfWeek }} - {{ booking.start | date }} {{ booking.start | time }} -
                            {{ booking.end | time }} Uhr
                            <span
                                :class="{
                            'text-green-500': booking.status === 'confirmed',
                            'text-red-500': booking.status === 'denied',
                            'text-yellow-500': booking.status === 'pending'
                        }">{{ booking.status | status }}
                            </span>
                        </td>
                        <td class="px-6 py-3">
                            {{ booking.user ? booking.user.name : '-' }}
                        </td>
                        <td class="px-6 py-3">
                            {{ booking.comment }}
                            <span v-show="booking.event?.title" class="text-gray-500">
                                <br><span class="text-sm font-medium text-gray-500">Veranstaltungstitel</span><br>
                                <span
                                    class="mr-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800">
                                    {{ booking.event?.title }}
                                </span>
                            </span>
                            <span v-if="booking.room_layout" class="text-gray-500">
                                <br><span class="text-sm font-medium text-gray-500">Raumlayout</span><br>
                                <span
                                    class="mr-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800">
                                    {{ booking.room_layout.name }}
                                </span>
                            </span>
                            <span v-if="booking.caterings.length" class="text-gray-500">
                                <br><span class="text-sm font-medium text-gray-500">Catering</span><br>
                                <span v-for="catering in booking.caterings" class="mr-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800">
                                                      {{ catering.name }}
                                       </span>
                            </span>
                            <span v-show="booking.postable_equipment.length > 0" class="text-gray-500">
                                <br><span class="text-sm font-medium text-gray-500">Zubuchbare Ausstattung</span><br>
                                <span v-for="equipment in booking.postable_equipment"
                                      class="mr-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800">
                                    {{ equipment.name }}
                                </span>
                            </span>
                            <span v-show="booking.event?.person" class="text-gray-500">
                                <br><span class="text-sm font-medium text-gray-500">Teilnehmeranzahl</span><br>
                                <span
                                    class="mr-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800">
                                    {{ booking.event?.person }}
                                </span>
                            </span>
                            <span v-show="booking.event?.phone" class="text-gray-500">
                                <br><span class="text-sm font-medium text-gray-500">Rufnummer für Rückfragen</span><br>
                                <span
                                    class="mr-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-800">
                                    {{ booking.event?.phone }}
                                </span>
                            </span>
                        </td>
                        <td class="px-6 py-3"><span
                            v-if="booking.conference_room_booking_series"> {{
                                booking.conference_room_booking_series.begin | date
                            }} - {{
                                booking.conference_room_booking_series.end | date
                            }} Frequenz: {{
                                booking.conference_room_booking_series.cycle
                            }} {{ getReadableInterval(booking.conference_room_booking_series.type) }}</span>
                        </td>
                        <td class="px-6 py-3 text-right flex" v-if="can.update.conference_room_booking">
                            <conference-room-booking-edit @changed="load" :booking="booking" :floors="floors"></conference-room-booking-edit>
                            <conference-room-booking-delete @deleted="load" :booking="booking" ></conference-room-booking-delete>
                        </td>
                    </tr>
                </tailwind-table>

                <div class="flex justify-end">
                    <conference-room-booking :room="item" @reload="load"></conference-room-booking>
                </div>

            </accordion-item>
        </accordion>

        <div v-show="items.length === 0 && !loading">
            Keine Konferenzräume gefunden.
        </div>

    </div>
</template>

<script>
import Headline from "../components/UI/Headline";
import Loading from "../components/UI/Loading";
import ConferenceRoomCreate from "../components/ConferenceRoomCreate";
import TailwindTable from "../components/UI/TailwindTable";
import Card from "../components/UI/Card";
import ConferenceRoomEdit from "../components/ConferenceRoomEdit";
import ConferenceRoomBookingCreate from "../components/ConferenceRoomBookingCreate";
import ButtonSecondary from "../components/UI/ButtonSecondary";
import ConfirmDialog from "../components/UI/ConfirmDialog";
import FullCard from "../components/UI/FullCard";
import CardFullInvisible from "../components/UI/CardFullInvisible";
import ButtonWhite from "../components/UI/ButtonWhite";
import ButtonPrimary from "../components/UI/ButtonPrimary";
import sortBy from "lodash/sortBy";
import ConferenceRoomBooking from "../components/ConferenceRoomBooking";
import ButtonDelete from "../components/UI/ButtonDelete";
import Accordion from "../components/UI/Accordion";
import AccordionItem from "../components/UI/AccordionItem";
import ConferenceRoomsEdit from "../components/ConferenceRoom/ConferenceRoomsEdit.vue";
import NavigationFloor from "../components/NavigationFloor.vue";
import SelectMenu from "../components/UI/SelectMenu.vue";
import InputText from "../components/UI/InputText.vue";
import SelectSmall from "../components/UI/SelectSmall.vue";
import ConferenceRoomNavigation from "../components/ConferenceRoomNavigation.vue";
import cloneDeep from "lodash/cloneDeep";
import ConferenceRoomBookingEdit from "../components/ConferenceRoomBookingEdit.vue";
import ConferenceRoomBookingDelete from "../components/ConferenceRoomBookingDelete.vue";

export default {
    name: "ConferenceRooms",
    components: {
        ConferenceRoomBookingDelete,
        ConferenceRoomNavigation,
        SelectSmall,
        InputText,
        SelectMenu,
        NavigationFloor,
        Accordion,
        AccordionItem,
        ButtonDelete,
        ButtonPrimary,
        ButtonSecondary,
        ButtonWhite,
        Card,
        CardFullInvisible,
        ConferenceRoomBooking,
        ConferenceRoomBookingCreate,
        ConferenceRoomBookingEdit,
        ConferenceRoomCreate,
        ConferenceRoomEdit,
        ConferenceRoomsEdit,
        ConfirmDialog,
        FullCard,
        Headline,
        Loading,
        TailwindTable,
    },

    data: () => ({
        conferenceRooms: [],
        items: [],
        loading: true,
        floors: [],
        floor: "all",
        available: [],
        unavailable: [],
        bookingToSwap: null,
        roomToSwap: null,
        roomToOpen: null,
        rooms: [],
    }),

    inject: ['can'],

    mounted() {
        this.roomToOpen = new URL(location.href).hash;
        this.roomToOpen = this.roomToOpen.replace('#room', '')

        this.load();
        this.loadFloors()
    },

    watch:{
        floor(val){
            if(val === "all"){
                this.conferenceRooms = this.items;
                this.available = [];
                this.unavailable = [];
                return;
            }
            if(val === "" || val == null){
                this.conferenceRooms = this.items.filter(i => !i.floor_id);
                this.available = [];
                this.unavailable = [];
                return;
            }
            this.conferenceRooms = this.items.filter(f => f.floor_id === parseInt(val));
            this.available = this.conferenceRooms.filter(i => {
                if(i.bookings.length > 0){
                    return !i.bookings.some(b => this.checkUnavailable(b));
                }else{
                    return true;
                }
            }) ;
            this.unavailable = this.conferenceRooms.filter(i => {
                if(i.bookings.length > 0){
                    return i.bookings.some(b => this.checkUnavailable(b));
                }else{
                    return false;
                }
            });
        }
    },

    filters: {
        time(val) {
            return val.substring(11, 16);
        },
        date(val) {
            let [y, m, d] = val.substring(0, 10).split('-');
            return `${d}.${m}.${y}`;
        },
        dayOfWeek(val) {
            let [y, m, d] = val.substring(0, 10).split('-');
            return new Date(`${y}-${m}-${d}`).toLocaleString('de-de', {  weekday: 'short' });
        },
        status(val) {
            switch (val){
                case 'confirmed' : return 'bestätigt'
                case 'denied' : return 'abgelehnt'
                case 'pending' : return 'ausstehend'
            }
        }
    },

    methods: {
        cloneDeep,
        async loadFloors(){
            const response = await axios.get('/admin/floors');
            this.floors = await response.data.data;
        },
        load() {
            this.loading = true;
            this.$http
                .get('/admin/conference_rooms')
                .then(response => {
                    this.items = response.data.data;
                    this.conferenceRooms = this.items;
                    this.available = this.items.filter(i => i.bookings.length === 0);
                    this.unavailable = this.items.filter(i => i.bookings.length > 0);
                    this.loading = false;

                  /*f// NEW: open dialog if ?roomId=... param exists
                  const urlParams = new URLSearchParams(window.location.search);
                  const roomIdFromQuery = urlParams.get('roomId');

                  if (roomIdFromQuery) {
                    const roomIdNum = parseInt(roomIdFromQuery, 10);
                    const room = this.conferenceRooms.find(i => i.id === roomIdNum);

                    if (room) {
                      this.bookConferenceRoom(room);
                    }
                  }*/

                    this.scrollTo(this.roomToOpen);
                })
                .catch(error => {
                    this.$root.$emit('error', error);
                })
        },
        remove(item,deleteSeries = false) {
            this.loading = true;
            this.$http
                .post(`/admin/conference_room_bookings/${item.id}`, {
                    params:{deleteSeries},
                    _method: 'delete',
                })
                .then(() => {
                    this.load();
                })
                .catch(error => {
                    this.loading = false;
                    this.$root.$emit('error', error);
                });
        },
        change(bookingToSwap, series = false) {
            this.loading = true;

            let params = {
                start: bookingToSwap.start,
                end: bookingToSwap.end,
                event: bookingToSwap.event,
                comment: bookingToSwap.comment ?? '',
                series: series,
                switch: bookingToSwap.conferenceRoom.id,
                postable_equipment: bookingToSwap.postableEquipment,
                room_layout: bookingToSwap.roomLayout,
                caterings: bookingToSwap.caterings,
                user_id: bookingToSwap.user?.id,
            };

            this.$http
                .put(`/admin/conference_room_bookings/${bookingToSwap.id}`, params)
                .then(() => {
                    this.load();
                })
                .catch(error => {
                    this.loading = false;
                    this.$root.$emit('error', error);
                });
        },
        sortBookings(bookings) {
            return sortBy(bookings, 'start');
        },
        checkUnavailable(booking) {
            let now = new Date();
            return !!(new Date(booking.start) <= now && now <= new Date(booking.end))
        },
        bookConferenceRoom(cr){
            this.$refs.create.bookRoom(cr);
        },
        getReadableInterval(interval) {
            switch (interval) {
                case "daily":
                    return "Tag(e)"
                case "weekly":
                    return "Woche(n)"
                case "monthly":
                    return "Monat(e)"
                case "yearly":
                    return "Jahr(e)"
            }
        },

        scrollTo(roomId) {
            this.$nextTick(()=> {
                if(this.$refs['room'+roomId]){
                    let element = this.$refs['room'+roomId][0] ?? null;

                    if(element){
                        element.$el.className = element.$el.className + ' bg-yellow-100'
                        element.$el.scrollIntoView()
                    }
                }
            })
        }
    }
}
</script>

<style scoped>

</style>
